import { Component, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ScoreClass } from '../../../utils/match-score.util';

export interface ScoreSelectOption {
  value: any;
  label: string;
  subtitle?: string;     // secondary line below label (e.g. date range or "Draft Plan")
  score?: number;        // 0–100 integer, displayed as "XX%"
  scoreClass?: ScoreClass;
  badge?: string;        // optional text badge (e.g. "CSA", "DRAFT")
  dividerAfter?: boolean; // render a separator line under this option (e.g. pinned "On Utility")
}

@Component({
  selector: 'app-score-select',
  standalone: true,
  imports: [CommonModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './score-select.component.html',
  styleUrl: './score-select.component.scss'
})
export class ScoreSelectComponent {
  private _options: ScoreSelectOption[] = [];
  private _value: any = null;

  @Input()
  set options(v: ScoreSelectOption[]) {
    this._options = v;
    this.updateSelectedOption();
    this.updateFilteredOptions();
  }
  get options(): ScoreSelectOption[] {
    return this._options;
  }

  @Input()
  set value(v: any) {
    this._value = v;
    this.updateSelectedOption();
  }
  get value(): any {
    return this._value;
  }

  @Input() label = '';
  @Input() disabled = false;
  @Input() loading = false;
  @Input() required = false;
  @Input() hasError = false;
  /** Renders a search box at the top of the panel that filters options by label/subtitle. */
  @Input() searchable = false;
  @Output() valueChange = new EventEmitter<any>();

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  /** Pre-computed — updated when options or value change. */
  selectedOption: ScoreSelectOption | null = null;
  /** Pre-computed — options matching the current search text (all options when not searching). */
  filteredOptions: ScoreSelectOption[] = [];
  searchText = '';

  onSelectionChange(newValue: any): void {
    this.valueChange.emit(newValue);
  }

  onPanelOpened(opened: boolean): void {
    if (!this.searchable) {
      return;
    }
    if (opened) {
      this.searchInput?.nativeElement?.focus();
    } else {
      this.onSearchChange('');
    }
  }

  onSearchChange(text: string): void {
    this.searchText = text;
    this.updateFilteredOptions();
  }

  /** Keep typing inside the search box from triggering mat-select's key handling. */
  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' && event.key !== 'Enter') {
      event.stopPropagation();
    }
  }

  private updateSelectedOption(): void {
    this.selectedOption = this._options.find(o => o.value === this._value) ?? null;
  }

  private updateFilteredOptions(): void {
    const text = this.searchText.trim().toLowerCase();
    this.filteredOptions = !text
      ? this._options
      : this._options.filter(o =>
          (o.label || '').toLowerCase().includes(text)
          || (o.subtitle || '').toLowerCase().includes(text));
  }
}
